import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardCheck,
  Eye,
  BarChart3,
  User,
  TestTube2,
  CheckCircle,
  Clock,
  ExternalLink,
  Search,
  ChevronDown,
  X,
  Trophy,
  Crown,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { formatDistanceToNow, format } from 'date-fns';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ErrorState, Skeleton } from '../../components/ui';

type EvaluationType = 'phase1' | 'phase2' | 'phase3';
type WorkflowStatus = 'phase1_running' | 'phase1_eval' | 'awaiting_phase2' | 'phase2_running' | 'phase2_eval' | 'completed';

interface EvaluationSet {
  id: string;
  startedAt: string;
  completedAt?: string;
  evaluatorRole?: string;
  rankings: string[];
  selectedBest?: string;
  evaluatorNotes?: string;
  evaluationType: EvaluationType;
  evaluator: {
    id: string;
    name: string;
    email: string;
  };
  testRun: {
    id: string;
    name: string;
    status: string;
    isRevealed: boolean;
    runType?: 'MODEL_COMPARISON' | 'MODEL_STABILITY';
    modelMapping?: Record<string, string>; // blindCode -> modelName (only when revealed)
    workflowStatus?: WorkflowStatus;
    testCase: {
      id: string;
      name: string;
      code: string;
      condition: string;
      category?: string;
    };
  };
  _count: {
    items: number;
  };
}

const STATUSES = [
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
];

const EVALUATION_TYPES = [
  { value: 'phase1', label: 'Phase 2+3 (Case Analysis)' },
  { value: 'phase2', label: 'Phase 4 (Care Provider Matching)' },
  { value: 'phase3', label: 'Phase 5 (Human Quality)' },
];

const CATEGORIES = [
  'ORTHOPEDIC',
  'CARDIAC',
  'NEUROLOGICAL',
  'ONCOLOGY',
  'GASTROENTEROLOGY',
  'PULMONOLOGY',
  'ENDOCRINE',
  'RHEUMATOLOGY',
  'DERMATOLOGY',
  'PSYCHIATRIC',
  'PEDIATRIC',
  'GERIATRIC',
  'OBSTETRIC',
  'UROLOGICAL',
  'OTHER',
];

function EvalStatusBadge({ completed }: { completed: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        completed
          ? 'bg-green-100 text-green-700'
          : 'bg-amber-100 text-amber-700'
      )}
    >
      {completed ? (
        <>
          <CheckCircle className="h-3 w-3" />
          Completed
        </>
      ) : (
        <>
          <Clock className="h-3 w-3" />
          In Progress
        </>
      )}
    </span>
  );
}

function WorkflowStatusBadge({ workflowStatus }: { workflowStatus?: WorkflowStatus }) {
  if (!workflowStatus) return null;

  const config: Record<WorkflowStatus, { color: string; label: string }> = {
    phase1_running: { color: 'bg-blue-50 text-blue-600', label: 'Phase 2+3 Running' },
    phase1_eval: { color: 'bg-amber-50 text-amber-600', label: 'Awaiting Phase 2+3 Eval' },
    awaiting_phase2: { color: 'bg-purple-50 text-purple-600', label: 'Ready for Phase 4' },
    phase2_running: { color: 'bg-blue-50 text-blue-600', label: 'Phase 4 Running' },
    phase2_eval: { color: 'bg-amber-50 text-amber-600', label: 'Awaiting Phase 4 Eval' },
    completed: { color: 'bg-green-50 text-green-600', label: 'Workflow Done' },
  };

  const cfg = config[workflowStatus];
  if (!cfg) return null;

  return (
    <span className={clsx('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium', cfg.color)}>
      {cfg.label}
    </span>
  );
}

export function EvaluationList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    testRunId: string;
    evaluationType: EvaluationType;
  } | null>(null);

  // Filter state from URL
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const evaluator = searchParams.get('evaluator') || '';
  const category = searchParams.get('category') || '';
  const evalType = searchParams.get('type') || '';
  const runType = searchParams.get('runType') || '';

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['validation', 'evaluations'],
    queryFn: () =>
      api.get<{
        data: EvaluationSet[];
        pagination: { total: number; limit: number; offset: number };
      }>('/api/validation/evaluations?limit=1000'),
  });

  const deleteMutation = useMutation({
    mutationFn: (params: { id: string; testRunId: string; evaluationType: EvaluationType }) => {
      // Use different endpoints based on evaluation type
      if (params.evaluationType === 'phase2') {
        // Search evaluation - uses different endpoint
        return api.delete(`/api/validation/search-evaluations/test-runs/${params.testRunId}/search-evaluation-sets/${params.id}`);
      } else if (params.evaluationType === 'phase3') {
        // Quality assessment - uses different endpoint
        return api.delete(`/api/validation/quality-assessment/test-runs/${params.testRunId}/sets/${params.id}`);
      } else {
        // Phase 1 (case analysis) evaluation
        return api.delete(`/api/validation/evaluations/${params.id}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] });
      setDeleteConfirm(null);
    },
  });

  const evaluations = response?.data || [];

  // Get unique evaluators for filter
  const uniqueEvaluators = useMemo(() => {
    const evaluators = new Map<string, string>();
    evaluations.forEach((e) => {
      evaluators.set(e.evaluator.id, e.evaluator.name);
    });
    return Array.from(evaluators.entries()).map(([id, name]) => ({ id, name }));
  }, [evaluations]);

  // Filter evaluations
  const filteredEvaluations = useMemo(() => {
    return evaluations.filter((evaluation) => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          evaluation.testRun.name.toLowerCase().includes(searchLower) ||
          evaluation.testRun.testCase.name.toLowerCase().includes(searchLower) ||
          evaluation.testRun.testCase.code.toLowerCase().includes(searchLower) ||
          evaluation.evaluator.name.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (status === 'completed' && !evaluation.completedAt) return false;
      if (status === 'in_progress' && evaluation.completedAt) return false;

      // Evaluator filter
      if (evaluator && evaluation.evaluator.id !== evaluator) return false;

      // Category filter
      if (category && evaluation.testRun.testCase.category !== category) return false;

      // Evaluation type filter
      if (evalType && evaluation.evaluationType !== evalType) return false;

      // Run type filter (MODEL_COMPARISON vs MODEL_STABILITY)
      if (runType && evaluation.testRun.runType !== runType) return false;

      return true;
    });
  }, [evaluations, search, status, evaluator, category, evalType, runType]);

  // Sort evaluations by date descending
  const sortedEvaluations = useMemo(() => {
    return [...filteredEvaluations].sort((a, b) => {
      const dateA = new Date(a.completedAt || a.startedAt).getTime();
      const dateB = new Date(b.completedAt || b.startedAt).getTime();
      return dateB - dateA; // Descending
    });
  }, [filteredEvaluations]);

  // Group evaluations by test run (each test run has Phase 1, Phase 2, and Phase 3)
  const groupedByTestRun = useMemo(() => {
    const groups = new Map<string, {
      testRunId: string;
      testRunName: string;
      testCase: EvaluationSet['testRun']['testCase'];
      isRevealed: boolean;
      runType?: 'MODEL_COMPARISON' | 'MODEL_STABILITY';
      workflowStatus?: WorkflowStatus;
      modelMapping?: Record<string, string>;
      phase1?: EvaluationSet;
      phase2?: EvaluationSet;
      phase3?: EvaluationSet;
      latestDate: Date;
    }>();

    sortedEvaluations.forEach((evaluation) => {
      const key = evaluation.testRun.id;
      const existing = groups.get(key);
      const evalDate = new Date(evaluation.completedAt || evaluation.startedAt);

      if (!existing) {
        groups.set(key, {
          testRunId: evaluation.testRun.id,
          testRunName: evaluation.testRun.name,
          testCase: evaluation.testRun.testCase,
          isRevealed: evaluation.testRun.isRevealed,
          runType: evaluation.testRun.runType,
          workflowStatus: evaluation.testRun.workflowStatus,
          modelMapping: evaluation.testRun.modelMapping,
          phase1: evaluation.evaluationType === 'phase1' ? evaluation : undefined,
          phase2: evaluation.evaluationType === 'phase2' ? evaluation : undefined,
          phase3: evaluation.evaluationType === 'phase3' ? evaluation : undefined,
          latestDate: evalDate,
        });
      } else {
        if (evaluation.evaluationType === 'phase1') {
          existing.phase1 = evaluation;
        } else if (evaluation.evaluationType === 'phase2') {
          existing.phase2 = evaluation;
        } else if (evaluation.evaluationType === 'phase3') {
          existing.phase3 = evaluation;
        }
        if (evalDate > existing.latestDate) {
          existing.latestDate = evalDate;
        }
        // Update revealed status if any evaluation shows it
        if (evaluation.testRun.isRevealed) {
          existing.isRevealed = true;
          existing.modelMapping = evaluation.testRun.modelMapping;
        }
        if (evaluation.testRun.workflowStatus) {
          existing.workflowStatus = evaluation.testRun.workflowStatus;
        }
        if (evaluation.testRun.runType) {
          existing.runType = evaluation.testRun.runType;
        }
      }
    });

    // Sort groups by latest date (descending)
    return Array.from(groups.values()).sort((a, b) =>
      b.latestDate.getTime() - a.latestDate.getTime()
    );
  }, [sortedEvaluations]);

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = search || status || evaluator || category || evalType || runType;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Evaluations</h1>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3"><Skeleton width={80} height={12} /></th>
                <th className="px-4 py-3"><Skeleton width={70} height={12} /></th>
                <th className="px-4 py-3"><Skeleton width={50} height={12} /></th>
                <th className="px-4 py-3"><Skeleton width={60} height={12} /></th>
                <th className="px-4 py-3"><Skeleton width={40} height={12} /></th>
                <th className="px-4 py-3"><Skeleton width={40} height={12} /></th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <Skeleton width="70%" height={14} />
                      <Skeleton width="50%" height={12} />
                    </div>
                  </td>
                  <td className="px-4 py-3"><Skeleton width={100} height={14} /></td>
                  <td className="px-4 py-3"><Skeleton width={80} height={24} variant="rectangular" /></td>
                  <td className="px-4 py-3"><Skeleton width={60} height={14} /></td>
                  <td className="px-4 py-3"><Skeleton width={30} height={14} /></td>
                  <td className="px-4 py-3"><Skeleton width={80} height={14} /></td>
                  <td className="px-4 py-3"><Skeleton width={80} height={24} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Evaluations</h1>
          </div>
        </div>
        <ErrorState
          title="Failed to load evaluations"
          message={error instanceof Error ? error.message : 'An unexpected error occurred'}
          onRetry={() => queryClient.invalidateQueries({ queryKey: ['validation', 'evaluations'] })}
          variant="card"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evaluations</h1>
          <p className="text-sm text-gray-500">
            {groupedByTestRun.length} test run{groupedByTestRun.length !== 1 ? 's' : ''} with evaluations
            <span className="text-gray-400 ml-1">({filteredEvaluations.length} total evaluations)</span>
          </p>
        </div>
      </div>

      {/* Enhanced Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {/* Search with clear button */}
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by test run, test case, or evaluator..."
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
          />
          {search && (
            <button
              onClick={() => updateFilter('search', '')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap gap-2">
          {/* Evaluation Type Filter */}
          <div className="relative">
            <select
              value={evalType}
              onChange={(e) => updateFilter('type', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                evalType
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">All Types</option>
              {EVALUATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              evalType ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                status
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">Status</option>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              status ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          {/* Evaluator Filter */}
          <div className="relative">
            <select
              value={evaluator}
              onChange={(e) => updateFilter('evaluator', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                evaluator
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">Evaluator</option>
              {uniqueEvaluators.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              evaluator ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <select
              value={category}
              onChange={(e) => updateFilter('category', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                category
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">Category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              category ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          {/* Run Type Filter */}
          <div className="relative">
            <select
              value={runType}
              onChange={(e) => updateFilter('runType', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                runType
                  ? runType === 'MODEL_STABILITY'
                    ? 'border-purple-300 bg-purple-50 text-purple-700'
                    : 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">All Run Types</option>
              <option value="MODEL_COMPARISON">Comparison</option>
              <option value="MODEL_STABILITY">Stability</option>
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              runType
                ? runType === 'MODEL_STABILITY' ? 'text-purple-500' : 'text-blue-500'
                : 'text-gray-400'
            )} />
          </div>
        </div>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors"
          >
            <X className="h-4 w-4" />
            Clear all
          </button>
        )}
      </div>

      {/* Error Display */}
      {deleteMutation.isError && (
        <ErrorState
          title="Failed to delete evaluation"
          message={deleteMutation.error instanceof Error ? deleteMutation.error.message : 'An unexpected error occurred'}
          variant="inline"
        />
      )}

      {/* Evaluations Grouped by Test Case */}
      {groupedByTestRun.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <ClipboardCheck className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {hasActiveFilters ? 'No evaluations match your filters' : 'No evaluations yet'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Evaluations will appear here when you complete blind evaluations on test runs.'}
          </p>
          {!hasActiveFilters && (
            <Link
              to="/validation/test-runs"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
            >
              <TestTube2 className="h-4 w-4" />
              View Test Runs
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedByTestRun.map((group) => {
            const isActuallyRevealed = group.isRevealed && group.workflowStatus === 'completed';
            const hasResults = isActuallyRevealed && (group.phase1?.completedAt || group.phase2?.completedAt);
            const resultsPath = group.runType === 'MODEL_STABILITY'
              ? `/validation/test-runs/${group.testRunId}/stability-results?from=evaluations`
              : `/validation/test-runs/${group.testRunId}/results?from=evaluations`;
            const primaryLink = hasResults ? resultsPath : `/validation/test-runs/${group.testRunId}`;

            const renderPhaseCard = (phase: EvaluationSet | undefined, phaseNumber: 1 | 2 | 3) => {
              const phaseLabel = phaseNumber === 1
                ? 'Phase 2+3: Case Analysis'
                : phaseNumber === 2
                  ? 'Phase 4: Care Provider Matching'
                  : 'Phase 5: Human Quality';
              const bgColor = phaseNumber === 1
                ? 'bg-blue-50 border-blue-200'
                : phaseNumber === 2
                  ? 'bg-purple-50 border-purple-200'
                  : 'bg-emerald-50 border-emerald-200';
              const textColor = phaseNumber === 1
                ? 'text-blue-700'
                : phaseNumber === 2
                  ? 'text-purple-700'
                  : 'text-emerald-700';
              const iconBg = phaseNumber === 1
                ? 'bg-blue-100'
                : phaseNumber === 2
                  ? 'bg-purple-100'
                  : 'bg-emerald-100';

              if (!phase) {
                return (
                  <div className={clsx('flex-1 rounded-lg border p-4', 'bg-gray-50 border-gray-200')}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={clsx('rounded-full p-1.5', 'bg-gray-100')}>
                        <ClipboardCheck className="h-4 w-4 text-gray-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-400">{phaseLabel}</span>
                    </div>
                    <p className="text-xs text-gray-400 italic">Not evaluated yet</p>
                  </div>
                );
              }

              const winner = phase.selectedBest || (phase.rankings.length > 0 ? phase.rankings[0] : null);
              const winnerDisplay = winner && isActuallyRevealed && group.modelMapping
                ? group.modelMapping[winner] || winner
                : winner;

              return (
                <div className={clsx('flex-1 rounded-lg border p-4', bgColor)}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={clsx('rounded-full p-1.5', iconBg)}>
                        <ClipboardCheck className={clsx('h-4 w-4', textColor)} />
                      </div>
                      <span className={clsx('text-sm font-medium', textColor)}>{phaseLabel}</span>
                    </div>
                    <EvalStatusBadge completed={!!phase.completedAt} />
                  </div>

                  <div className="space-y-2">
                    {/* Evaluator */}
                    <div className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-600">{phase.evaluator.name}</span>
                    </div>

                    {/* Winner */}
                    {winnerDisplay ? (
                      <div className="flex items-center gap-2">
                        <Crown className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-xs font-medium text-amber-700">{winnerDisplay}</span>
                        {phase.rankings.length > 1 && (
                          <span className="text-xs text-gray-400">
                            ({phase.rankings.length} ranked)
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Trophy className="h-3.5 w-3.5 text-gray-300" />
                        <span className="text-xs text-gray-400">No winner selected</span>
                      </div>
                    )}

                    {/* Date */}
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {phase.completedAt
                          ? format(new Date(phase.completedAt), 'MMM d, yyyy')
                          : formatDistanceToNow(new Date(phase.startedAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>

                  {/* Actions: View & Delete */}
                  <div className="mt-3 pt-3 border-t border-gray-200/50 flex items-center justify-between">
                    <Link
                      to={
                        phaseNumber === 3
                          ? `/validation/test-runs/${group.testRunId}/quality-assessment`
                          : group.runType === 'MODEL_STABILITY'
                            ? `/validation/test-runs/${group.testRunId}/${phaseNumber === 1 ? 'stability-evaluate' : 'search-stability-evaluate'}`
                            : `/validation/test-runs/${group.testRunId}/${phaseNumber === 1 ? 'evaluate' : 'search-evaluate'}`
                      }
                      className={clsx(
                        'inline-flex items-center gap-1 text-xs font-medium transition-colors',
                        phaseNumber === 1
                          ? 'text-blue-600 hover:text-blue-700'
                          : phaseNumber === 2
                            ? 'text-purple-600 hover:text-purple-700'
                            : 'text-emerald-600 hover:text-emerald-700'
                      )}
                    >
                      <Eye className="h-3 w-3" />
                      View evaluation
                    </Link>
                    <button
                      onClick={() => setDeleteConfirm({
                        id: phase.id,
                        testRunId: group.testRunId,
                        evaluationType: phase.evaluationType,
                      })}
                      className="text-xs text-gray-400 hover:text-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            };

            return (
              <div
                key={group.testRunId}
                className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <Link
                        to={primaryLink}
                        className="font-semibold text-gray-900 hover:text-amber-600 truncate"
                      >
                        {group.testRunName}
                      </Link>
                      {group.runType && (
                        <span className={clsx(
                          'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
                          group.runType === 'MODEL_STABILITY'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        )}>
                          {group.runType === 'MODEL_STABILITY' ? 'Stability' : 'Comparison'}
                        </span>
                      )}
                      <WorkflowStatusBadge workflowStatus={group.workflowStatus} />
                      {isActuallyRevealed && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Eye className="h-3 w-3" />
                          Revealed
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Link
                        to={`/validation/test-cases/${group.testCase.id}`}
                        className="text-sm text-gray-500 hover:text-amber-600"
                      >
                        {group.testCase.name}
                      </Link>
                      <span className="text-xs font-mono text-gray-400">
                        {group.testCase.code}
                      </span>
                      {group.testCase.category && (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">
                          {group.testCase.category.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {hasResults && (
                      <Link
                        to={resultsPath}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-200 transition-colors"
                      >
                        <BarChart3 className="h-4 w-4" />
                        View Results
                      </Link>
                    )}
                    <Link
                      to={`/validation/test-runs/${group.testRunId}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Test Run
                    </Link>
                  </div>
                </div>

                {/* Phase Cards */}
                <div className="p-5">
                  <div className={`grid gap-4 ${group.runType === 'MODEL_STABILITY' ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {renderPhaseCard(group.phase1, 1)}
                    {renderPhaseCard(group.phase2, 2)}
                    {/* Phase 5 (Human Quality) only for comparison mode */}
                    {group.runType !== 'MODEL_STABILITY' && renderPhaseCard(group.phase3, 3)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            deleteMutation.mutate(deleteConfirm);
          }
        }}
        title="Delete Evaluation"
        message="Are you sure you want to delete this evaluation? This action cannot be undone. The evaluation and all its ratings will be permanently deleted."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
